import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { Badge, Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { isCampaignRunning, tcDisplayName } from '../../../api/models'
import { WarningTriangleIcon } from 'images'
import InfoBox from 'components/Shared/InfoBox'

export const tcLabelOptions = {
  selected: 'selected',
  select: 'select',
  selectDisabled: 'select-disabled',
  saved: 'saved',
  pending: 'pending',
  ignored: 'ignored',
  completed: 'completed',
}

function TasteClusterStatus({ status, onClick = null, disabled = false, disabledTooltip = null }) {
  const { t } = useTranslation()

  if (!status) {
    return null
  }

  const isDisabled =
    disabled ||
    [tcLabelOptions.selectDisabled, tcLabelOptions.pending, tcLabelOptions.ignored, tcLabelOptions.completed].some(
      (x) => x === status,
    )

  const button = (
    <Button
      onClick={onClick}
      disabled={isDisabled}
      className={classNames({
        'campaign-header_tc-status': true,
        [status]: true,
        clickable: !!onClick,
      })}
    >
      <>{t(`Recommendations.campaign.${status}`)}</>
    </Button>
  )

  if (disabled && disabledTooltip) {
    return (
      <InfoBox body={disabledTooltip} withAnchor={false}>
        {button}
      </InfoBox>
    )
  }

  return button
}

TasteClusterStatus.propTypes = {
  status: PropTypes.oneOf(Object.values(tcLabelOptions)),
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  disabledTooltip: PropTypes.string,
}

/**
 * Campaign header.
 * @Tags( recommendations, campaigns )
 */
export default function CampaignHeader({
  campaign,
  headerStatus,
  onTcLabelClick,
  children,
  onClick = null,
  className = '',
  showActiveBadge = true,
  hasValidationErrors = false,
  tcLabelDisabled = false,
  tcLabelDisabledTooltip = null,
}) {
  const { t } = useTranslation()

  const isRunning = isCampaignRunning(campaign, { validateNotEnded: true })

  return (
    <div className={`campaign-header ${className}`}>
      <div className="campaign-header_toggle btn btn-link" onClick={onClick}>
        <div className="campaign-header_toggle__title display-flex w-100">
          <div className="campaign-header_toggle__title__wrapper">
            <span
              className={classNames({
                title: true,
                lonely: !isRunning,
              })}
              title={tcDisplayName(campaign)}
            >
              {t('Recommendations.campaign.recommendationTitle', {
                tc: tcDisplayName(campaign),
              })}
            </span>

            {showActiveBadge && isRunning && <Badge className="active">{t('Recommendations.campaign.active')}</Badge>}
            
            {hasValidationErrors && (
              <div className="campaign-header_toggle__warning-icon" style={{ marginLeft: 'auto' }}>
                <WarningTriangleIcon width={18} height={18} />
              </div>
            )}
          </div>
        </div>
      </div>
      <TasteClusterStatus
        status={headerStatus}
        onClick={onTcLabelClick}
        disabled={tcLabelDisabled}
        disabledTooltip={tcLabelDisabledTooltip}
      />
      {children}
    </div>
  )
}

CampaignHeader.propTypes = {
  campaign: PropTypes.object.isRequired,
  headerStatus: PropTypes.oneOf(Object.values(tcLabelOptions)),
  onClick: PropTypes.func,
  children: PropTypes.node,
  onTcLabelClick: PropTypes.func,
  className: PropTypes.string,
  showActiveBadge: PropTypes.bool,
  hasValidationErrors: PropTypes.bool,
  tcLabelDisabled: PropTypes.bool,
  tcLabelDisabledTooltip: PropTypes.string,
}
